import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

// Types for our pin data
interface PinData {
  lat: number;
  lng: number;
  address?: string;
  placeId?: string;
  name?: string;
}

interface MapProps {
  onPinDropped?: (pinData: PinData) => void;
  onLocationSelected?: (location: PinData) => void;
  initialCenter?: { lat: number; lng: number };
  zoom?: number;
  height?: string;
  width?: string;
  className?: string;
}

interface GoogleMapComponentProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  onPinDropped?: (pinData: PinData) => void;
  onLocationSelected?: (location: PinData) => void;
  height?: string;
  width?: string;
}

// Google Maps component
const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  center,
  zoom,
  onPinDropped,
  onLocationSelected,
  height = "400px",
  width = "100%"
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();
  // const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  // const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>();

  // Initialize map
  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      });

      const newGeocoder = new window.google.maps.Geocoder();
      // const newPlacesService = new window.google.maps.places.PlacesService(newMap);

      setMap(newMap);
      // setGeocoder(newGeocoder);
      // setPlacesService(newPlacesService);

      // Add click listener for dropping pins
      newMap.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          handleMapClick(e.latLng, newMap, newGeocoder);
        }
      });
    }
  }, [ref, map, center, zoom]);

  // Handle map click to drop pin
  const handleMapClick = useCallback(
    (latLng: google.maps.LatLng, mapInstance: google.maps.Map, geocoderInstance: google.maps.Geocoder) => {
      // Remove existing marker
      if (marker) {
        marker.setMap(null);
      }

      // Create new marker
      const newMarker = new window.google.maps.Marker({
        position: latLng,
        map: mapInstance,
        draggable: true,
        animation: google.maps.Animation.DROP,
        title: 'Dropped Pin',
      });

      setMarker(newMarker);

      // Get address for the location
      geocoderInstance.geocode(
        { location: latLng },
        (results: google.maps.GeocoderResult[] | null, status: any) => {
          let address = 'Unknown location';
          let placeId = undefined;
          
          if (status === 'OK' && results && results[0]) {
            address = results[0].formatted_address;
            placeId = results[0].place_id;
          }

          const pinData: PinData = {
            lat: latLng.lat(),
            lng: latLng.lng(),
            address,
            placeId,
            name: address.split(',')[0], // Use first part of address as name
          };

          if (onPinDropped) {
            onPinDropped(pinData);
          }
        }
      );

      // Add drag listener to marker
      newMarker.addListener('dragend', () => {
        const position = newMarker.getPosition();
        if (position) {
          geocoderInstance.geocode(
            { location: position },
            (results: google.maps.GeocoderResult[] | null, status: any) => {
              let address = 'Unknown location';
              let placeId = undefined;
              
              if (status === 'OK' && results && results[0]) {
                address = results[0].formatted_address;
                placeId = results[0].place_id;
              }

              const pinData: PinData = {
                lat: position.lat(),
                lng: position.lng(),
                address,
                placeId,
                name: address.split(',')[0],
              };

              if (onPinDropped) {
                onPinDropped(pinData);
              }
            }
          );
        }
      });
    },
    [marker, onPinDropped]
  );

  return <div ref={ref} style={{ height, width }} className="rounded-lg" />;
};

// Search component
const SearchBox: React.FC<{
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  placeholder?: string;
}> = ({ onPlaceSelected, placeholder = "Search for a location..." }) => {
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete>();

  useEffect(() => {
    if (searchInputRef.current && window.google) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        fields: ['place_id', 'geometry', 'name', 'formatted_address'],
        types: ['establishment', 'geocode'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.geometry) {
          onPlaceSelected(place);
          setSearchValue(place.formatted_address || place.name || '');
        }
      });

      autocompleteRef.current = autocomplete;
    }
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg
          className="h-5 w-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <input
        ref={searchInputRef}
        type="text"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        placeholder={placeholder}
      />
    </div>
  );
};

// Loading component
const MapLoading: React.FC = () => (
  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading Google Maps...</p>
    </div>
  </div>
);

// Error component
const MapError: React.FC<{ error: Error }> = ({ error }) => (
  <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg border border-red-200">
    <div className="text-center">
      <div className="text-red-500 mb-4">
        <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 15.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <p className="text-red-700 font-medium">Failed to load Google Maps</p>
      <p className="text-red-600 text-sm mt-1">{error.message}</p>
    </div>
  </div>
);

// Main Map component with render function
const render = (status: Status): React.ReactElement => {
  switch (status) {
    case Status.LOADING:
      return <MapLoading />;
    case Status.FAILURE:
      return <MapError error={new Error('Failed to load Google Maps')} />;
    case Status.SUCCESS:
      return <div />; // Placeholder div that will be replaced by the actual map
    default:
      return <MapLoading />;
  }
};

// Main Map interface component
const Map: React.FC<MapProps> = ({
  onPinDropped,
  onLocationSelected,
  initialCenter = { lat: -1.2864, lng: 36.8172 }, // Default to Nairobi
  zoom = 12,
  height = "400px",
  width = "100%",
  className = "",
}) => {
  const [selectedLocation, setSelectedLocation] = useState<PinData | null>(null);
  const [center, setCenter] = useState(initialCenter);
  const [currentZoom, setCurrentZoom] = useState(zoom);

  // Get Google Maps API key from environment
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-yellow-500 mr-3">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-yellow-800 font-medium">Google Maps API Key Required</h3>
            <p className="text-yellow-700 text-sm mt-1">
              Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    if (place.geometry?.location) {
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setCenter(location);
      setCurrentZoom(15);

      const locationData: PinData = {
        lat: location.lat,
        lng: location.lng,
        address: place.formatted_address,
        placeId: place.place_id,
        name: place.name,
      };

      setSelectedLocation(locationData);

      if (onLocationSelected) {
        onLocationSelected(locationData);
      }
    }
  };

  const handlePinDropped = (pinData: PinData) => {
    setSelectedLocation(pinData);
    if (onPinDropped) {
      onPinDropped(pinData);
    }
  };

  const handleSubmitLocation = () => {
    if (selectedLocation && onLocationSelected) {
      onLocationSelected(selectedLocation);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Box */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Search Location</h3>
        <Wrapper
          apiKey={apiKey}
          render={render}
          libraries={['places']}
        >
          <SearchBox
            onPlaceSelected={handlePlaceSelected}
            placeholder="Search for a location or address..."
          />
        </Wrapper>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Interactive Map</h3>
          <p className="text-sm text-gray-600 mt-1">
            Click anywhere on the map to drop a pin, or search for a location above
          </p>
        </div>

        <Wrapper
          apiKey={apiKey}
          render={render}
          libraries={['places']}
        >
          <GoogleMapComponent
            center={center}
            zoom={currentZoom}
            onPinDropped={handlePinDropped}
            onLocationSelected={onLocationSelected}
            height={height}
            width={width}
          />
        </Wrapper>
      </div>

      {/* Selected Location Info */}
      {selectedLocation && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h4 className="text-lg font-medium text-gray-900 mb-2">📍 Selected Location</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Name:</span>
                  <span className="ml-2 text-gray-600">{selectedLocation.name || 'Unnamed location'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Address:</span>
                  <span className="ml-2 text-gray-600">{selectedLocation.address || 'No address available'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Coordinates:</span>
                  <span className="ml-2 text-gray-600 font-mono">
                    {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </span>
                </div>
                {selectedLocation.placeId && (
                  <div>
                    <span className="font-medium text-gray-700">Place ID:</span>
                    <span className="ml-2 text-gray-600 font-mono text-xs">{selectedLocation.placeId}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ml-4 flex flex-col space-y-2">
              <button
                onClick={handleSubmitLocation}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                ✓ Use This Location
              </button>
              <button
                onClick={() => {
                  setSelectedLocation(null);
                  navigator.clipboard.writeText(`${selectedLocation.lat}, ${selectedLocation.lng}`);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm"
              >
                📋 Copy Coordinates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">How to use:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• 🔍 <strong>Search:</strong> Type a location in the search box above</li>
          <li>• 📍 <strong>Drop Pin:</strong> Click anywhere on the map to place a pin</li>
          <li>• 🖱️ <strong>Move Pin:</strong> Drag the pin to a new location</li>
          <li>• ✅ <strong>Submit:</strong> Click "Use This Location" to confirm your selection</li>
        </ul>
      </div>
    </div>
  );
};

export default Map;
